import React from 'react';
import { LiveClassesListProps, LiveClassProps } from './types';
import style from './style.module.scss';
import cx from 'classnames';
import moment from 'moment-timezone';
import { Badge } from 'react-bootstrap';
import { UpgradeBadge } from '@magoosh/lib/info_badges';
import { DefaultButton, SecondaryButton } from '@magoosh/lib/button';
import { useYourClassesContext } from '../../all_classes/context';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import { paths } from 'config/path_helpers';
import { pluralize } from 'utilities/pluralize';
import Desk from '@magoosh/assets/desk.svg';
import DeskComplete from '@magoosh/assets/desk_complete.svg';
import StatusBadge from '@magoosh/pages/sectioned_assignments/organisms/status_badge';

export const LiveClass: React.FC<LiveClassProps> = (props) => {
  const { toggleOpenRegistrationFormModal } = useYourClassesContext();

  const dateTime = moment.utc(props.dateTime).tz(moment.tz.guess());
  const dateDiff = dateTime.diff(moment(), 'days');

  const button = {
    watchRecording: (
      <DefaultButton href={props.recordingUrl} target="_blank">
        Watch Recording
      </DefaultButton>
    ),
    recordingSoon: (
      <p style={{ margin: 0 }}>
        <strong>Waiting for Recording</strong>
      </p>
    ),
    joinNow: (
      <SecondaryButton href={props.joinUrl} target="_blank">
        Join Now
      </SecondaryButton>
    ),
    noRecording: (
      <p>
        <strong>No Recording Available</strong>
      </p>
    ),
    register: <DefaultButton onClick={() => toggleOpenRegistrationFormModal(props)}>Register</DefaultButton>,
    startSoon: (
      <p style={{ color: style.brandSecondary, margin: 0 }}>
        <strong>
          Starts {dateDiff === 0 ? 'Soon' : dateDiff > 1 ? `in ${pluralize(dateDiff, 'day')}` : 'Tomorrow'}
        </strong>
      </p>
    ),
    upgrade: (
      <a href="/about-live-classes" className={'btn btn-default'}>
        Upgrade
      </a>
    )
  };

  return (
    <div className={style.liveClass}>
      <div className={style.date}>
        {props.status === 'joinNow' && (
          <Badge className={style.badge}>
            <span>Live Now</span>
          </Badge>
        )}

        {props.dateTime && (
          <>
            {' '}
            <p className="u-margin-A-n">
              <small>{dateTime.isSame(moment(), 'day') ? 'Today' : dateTime.format('dddd, MMMM D')}</small>
            </p>
            <p className="u-margin-A-n">
              <small>{dateTime.format('LT z')}</small>
            </p>{' '}
          </>
        )}
      </div>
      <div className={style.name}>
        <p className="u-margin-A-n">
          <strong>{props.name}</strong>
        </p>
        {!props.hideDescription && (
          <>
            <p className="u-margin-A-n">
              {props.description}
              {props.status == 'upgrade' && (
                <>
                  <br />
                  <UpgradeBadge className="u-margin-R-xs" />
                </>
              )}
            </p>
            {props.status != 'upgrade' && props.resourceLinks.length > 0 && (
              <p>
                <strong>
                  Resources:{' '}
                  {props.resourceLinks.map((link, index) => {
                    return (
                      <>
                        <a href={link.url} target="_blank">
                          {link.name}
                        </a>
                        {index < props.resourceLinks.length - 1 ? ', ' : ''}
                      </>
                    );
                  })}
                </strong>
              </p>
            )}
            {props.assignments.map((assignment) => (
              <div key={assignment.id} className={style.assignment}>
                <div className={style.assignmentIcon}>
                  {assignment.submitted ? (
                    <img alt="DeskComplete" src={DeskComplete} />
                  ) : (
                    <img alt="Desk" src={Desk} />
                  )}
                </div>
                <div className={style.assignmentTitle}>
                  <a href={paths.contentBlockAssignment(assignment)}>{assignment.title}</a>
                </div>
                <div className={style.assignmentDueDate}>
                  {assignment.dueDate && (
                    <div>
                      Due {moment(assignment.dueDate).format('l')} by{' '}
                      {moment(assignment.dueDate).format('h:mma')} (Pacific)
                    </div>
                  )}
                </div>
                <div className={style.assignmentBadge}>
                  <StatusBadge dueDate={assignment.dueDate} submitted={assignment.submitted} />
                </div>
              </div>
            ))}
          </>
        )}
      </div>
      {status !== 'signUp' && <div className={style.button}>{button[props.status]}</div>}
    </div>
  );
};

export const LiveClassesList: React.FC<LiveClassesListProps> = (props) => {
  const { isLoading } = useYourClassesContext();
  return (
    <div className={cx(style.liveClassList, 'u-margin-T-m')}>
      <div className={cx(style.header, 'u-padding-B-s')}>
        <div className={style.icon}>{props.icon}</div>
        <div>
          <h3 className="u-margin-A-n">
            <strong>{props.title}</strong>
          </h3>
          <p className="u-margin-A-n" dangerouslySetInnerHTML={{ __html: props.description }} />
          <p>
            Access your live classes here. 5 minutes before your class starts, a "Join Now" button will
            appear.
          </p>
        </div>
      </div>
      {isLoading ? (
        <div className="text-center">
          <FontAwesomeIcon icon="spinner" className="fa-pulse fa-3x" />
        </div>
      ) : (
        <div>
          {props.liveClasses.map((liveClass) => (
            <LiveClass key={liveClass.id} {...liveClass} />
          ))}
        </div>
      )}
    </div>
  );
};

export default LiveClassesList;
